import Router from 'next/router';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { QueryParamNames } from '~/constants/search';
import { isFlattenedViewSelector } from '~/store/router/selectors';
import { getPathWithoutQuery, pushWithExistingQuery } from '~/utils/PathUtils';

export type UseIsFlattenedViewOptions = {
  /**
   * When true, the hook reports `isFlattenedView: false` regardless of URL state,
   * and `setIsFlattenedView` is a no-op. Used on public share surfaces where the
   * creator has locked the expand-nested control — callers compute this from
   * `presentationSettings.lockedControls.expandNested` and pass it in, keeping
   * the hook itself free of any share-link context coupling.
   */
  disabled?: boolean;
};

export const useIsFlattenedView = ({ disabled = false }: UseIsFlattenedViewOptions = {}) => {
  const isFlattenedView = useSelector(isFlattenedViewSelector);

  const setIsFlattenedView = useCallback(
    (isFlattened: boolean) => {
      if (disabled) {
        return;
      }
      const isFlatString = isFlattened ? 'true' : 'false';

      if (isFlatString === isFlattenedView) {
        return;
      }

      return isFlattened
        ? pushWithExistingQuery({
            path: getPathWithoutQuery(Router.asPath),
            newQuery: { [QueryParamNames.isFlattenedView]: true },
          })
        : pushWithExistingQuery({
            path: getPathWithoutQuery(Router.asPath),
            newQuery: { [QueryParamNames.isFlattenedView]: null },
          });
    },
    [disabled, isFlattenedView],
  );

  return {
    isFlattenedView: !disabled && isFlattenedView === 'true',
    setIsFlattenedView,
  };
};
